/**
 * Rendering a `Decision` — the answer to "why can Sarah see this".
 *
 * §4.8: both views "render the columns §4.7 already returns (`grant_id`,
 * `granted_on_type`, `granted_on_id`, `distance`, `principal_type`)", and
 * neither gets its own resolution logic. Nothing in this file computes an
 * outcome. Every value shown arrives from the resolver; what is added here is a
 * name for a uuid and a sentence for a tuple.
 *
 * The three chips map one-to-one onto the three `ORDER BY` terms in §4.7 — how
 * near the deciding grant sits, whether it names a person or a role, and what
 * it says. Read together, they are the precedence rules of §4.6 applied to this
 * row, which is what makes the answer checkable by someone who does not hold
 * those rules in their head.
 */

import type { Decision } from '../api/client.ts';
import { Badge } from './ui/badge.tsx';
import { Mono } from './ui/layout.tsx';

/** Resolves a grant subject id to something readable, falling back to the id. */
export type SubjectNamer = (type: 'node' | 'space', id: string) => string | undefined;

export function distanceLabel(distance: number | null): string {
  if (distance === null) return '—';
  if (distance === 0) return 'on the node itself';
  if (distance === 1) return '1 level up';
  return `${distance} levels up`;
}

export function VerdictBadge({ decision }: { decision: Decision }) {
  if (decision.allowed) return <Badge tone="success">Allowed</Badge>;
  // An explicit deny and "nothing reached this" are both denied, and §4.8 cares
  // about the difference: one is a decision someone made, the other is §4.6
  // rule 4 — the absence of any grant at all.
  return decision.grantId ? (
    <Badge tone="error">Denied</Badge>
  ) : (
    <Badge tone="gray">No grant</Badge>
  );
}

/**
 * The three precedence coordinates, side by side. Absent entirely when no grant
 * was found, because there is nothing that decided — which is itself the answer.
 */
export function PrecedenceChips({ decision }: { decision: Decision }) {
  if (!decision.grantId) {
    return (
      <span className="text-xs text-tertiary">Rule 4 — nothing found in the chain, so denied.</span>
    );
  }

  return (
    <span className="flex flex-wrap items-center gap-1.5">
      <Badge tone="outline">
        <span className="text-quaternary">distance</span>&nbsp;
        <span className="font-mono">{decision.distance}</span>
      </Badge>
      <Badge tone={decision.principalType === 'person' ? 'brand' : 'gray'}>
        {decision.principalType === 'person' ? 'person-specific' : 'role-based'}
      </Badge>
      <Badge tone={decision.allowed ? 'success' : 'error'} mono>
        {decision.allowed ? 'allow' : 'deny'}
      </Badge>
    </span>
  );
}

/**
 * Where the deciding grant sits: which subject, named where a name is known,
 * and how far above the node it is.
 */
export function GrantSite({ decision, nameOf }: { decision: Decision; nameOf?: SubjectNamer }) {
  if (!decision.grantId || !decision.grantedOnType || !decision.grantedOnId) {
    return <span className="text-sm text-tertiary">—</span>;
  }

  const name = nameOf?.(decision.grantedOnType, decision.grantedOnId);

  return (
    <span className="flex flex-col gap-0.5">
      <span className="text-sm text-primary">
        <span className="text-tertiary">{decision.grantedOnType}</span>{' '}
        {name ?? <Mono>{decision.grantedOnId}</Mono>}
      </span>
      <span className="text-xs text-tertiary">{distanceLabel(decision.distance)}</span>
    </span>
  );
}

/**
 * The whole explanation in one block, for places with room for a sentence.
 *
 * `explanation` is the API's own sentence, built from the same `Decision`; it
 * is rendered rather than re-derived so that the words a person reads here and
 * the words in a 403 body are the same words.
 */
export function DecisionExplanation({
  decision,
  explanation,
  nameOf,
}: {
  decision: Decision;
  explanation?: string;
  nameOf?: SubjectNamer;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-wrap items-center gap-2">
        <VerdictBadge decision={decision} />
        <PrecedenceChips decision={decision} />
      </div>
      {explanation ? <p className="text-sm text-tertiary">{explanation}</p> : null}
      {decision.grantId ? (
        <p className="text-xs text-quaternary">
          grant <Mono>{decision.grantId}</Mono> on{' '}
          {decision.grantedOnType && decision.grantedOnId
            ? (nameOf?.(decision.grantedOnType, decision.grantedOnId) ?? decision.grantedOnId)
            : '—'}
        </p>
      ) : null}
    </div>
  );
}

/**
 * §4.6, stated once and rendered beside both views.
 *
 * The rules are short enough to print, and printing them is what turns the
 * chips above from decoration into something a person can check an answer
 * against. [ADR 0003](docs/adr/0003-permission-resolution-precedence.md) is the
 * accepted decision behind the ordering.
 */
export function PrecedenceLegend() {
  return (
    <ol className="flex flex-col gap-1 text-xs text-tertiary">
      <li>
        <span className="font-semibold text-secondary">1.</span> Nearest ancestor wins — lowest
        distance from the node.
      </li>
      <li>
        <span className="font-semibold text-secondary">2.</span> At the same distance,
        person-specific beats role-based.
      </li>
      <li>
        <span className="font-semibold text-secondary">3.</span> At the same distance and
        specificity, deny beats allow.
      </li>
      <li>
        <span className="font-semibold text-secondary">4.</span> No grant anywhere in the chain
        means denied.
      </li>
    </ol>
  );
}
